import React from 'react';
import { motion } from 'framer-motion';
import { CheckCircle2, Star } from 'lucide-react';
import { CTAButton } from '../ui/CTAButton';
export function Hero() {
  const benefits = ['Rutinas de 20 minutos', 'Sin gimnasio', 'Sin equipamiento caro', 'Ideal para principiantes'];
  return <section className="relative overflow-hidden bg-gradient-to-b from-rose-50 to-white pt-16 pb-24 lg:pt-24 lg:pb-32" data-id="element-91">
      {/* Background decorative elements */}
      <div className="absolute top-0 left-0 w-full h-full overflow-hidden -z-10" data-id="element-92">
        <div className="absolute -top-[20%] -right-[10%] w-[70%] h-[70%] rounded-full bg-rose-100/50 blur-3xl opacity-60" data-id="element-93" />
        <div className="absolute top-[40%] -left-[10%] w-[50%] h-[50%] rounded-full bg-rose-200/30 blur-3xl opacity-60" data-id="element-94" />
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-95">
        <div className="grid lg:grid-cols-2 gap-12 lg:gap-8 items-center" data-id="element-96">
          {/* Text Content */}
          <motion.div className="max-w-2xl" initial={{
          opacity: 0,
          x: -30
        }} animate={{
          opacity: 1,
          x: 0
        }} transition={{
          duration: 0.6
        }} data-id="element-97">
            <div className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-rose-100 text-rose-700 font-semibold text-sm mb-6" data-id="element-98">
              <Star className="w-4 h-4 fill-rose-500 text-rose-500" data-id="element-99" />
              <span data-id="element-100">Más de 10,000 personas ya empezaron</span>
            </div>

            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-gray-900 leading-[1.1] mb-6" data-id="element-101">
              Transformá tu cuerpo y creá el hábito de entrenar en{' '}
              <span className="text-rose-500 relative whitespace-nowrap" data-id="element-102">
                solo 30 días
                <svg className="absolute -bottom-2 left-0 w-full h-3 text-rose-300 -z-10" viewBox="0 0 100 10" preserveAspectRatio="none" data-id="element-103">
                  <path d="M0 5 Q 50 10 100 5" stroke="currentColor" strokeWidth="4" fill="none" data-id="element-104" />
                </svg>
              </span>
              <br className="hidden md:block" data-id="element-105" /> — sin gimnasio ni rutinas
              complicadas.
            </h1>

            <p className="text-lg sm:text-xl text-gray-600 mb-8 leading-relaxed" data-id="element-106">
              Con <strong className="text-gray-900" data-id="element-107">Ejercitá en Casa</strong>{' '}
              vas a poder mejorar tu fuerza, energía y disciplina con rutinas
              simples de solo 20 minutos, incluso si hoy no tenés tiempo o nunca
              lograste mantener el ejercicio.
            </p>

            <ul className="grid sm:grid-cols-2 gap-3 mb-10" data-id="element-108">
              {benefits.map((benefit, index) => <motion.li key={index} className="flex items-center gap-2.5 text-gray-700 font-medium" initial={{
              opacity: 0,
              y: 10
            }} animate={{
              opacity: 1,
              y: 0
            }} transition={{
              delay: 0.3 + index * 0.1
            }} data-id="element-109">
                  <CheckCircle2 className="w-5 h-5 text-rose-500 flex-shrink-0" data-id="element-110" />
                  {benefit}
                </motion.li>)}
            </ul>

            <motion.div initial={{
            opacity: 0,
            y: 20
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            delay: 0.7
          }} className="flex flex-col sm:flex-row items-start sm:items-center gap-4" data-id="element-111">
              <CTAButton className="w-full sm:w-auto" data-id="element-112" />
            </motion.div>
          </motion.div>

          {/* Image/Mockup */}
          <motion.div className="relative lg:ml-auto w-full max-w-lg mx-auto" initial={{
          opacity: 0,
          scale: 0.9
        }} animate={{
          opacity: 1,
          scale: 1
        }} transition={{
          duration: 0.6,
          delay: 0.2
        }} data-id="element-113">
            <div className="relative" data-id="element-114">
              {/* Floating emojis around the ebook */}
              <motion.span className="absolute -top-4 -left-2 text-3xl md:text-4xl z-10 select-none" animate={{
              y: [0, -8, 0]
            }} transition={{
              duration: 2.5,
              repeat: Infinity,
              ease: 'easeInOut'
            }} data-id="element-115">
                💪
              </motion.span>
              <motion.span className="absolute top-8 -right-2 md:-right-4 text-3xl md:text-4xl z-10 select-none" animate={{
              y: [0, -10, 0]
            }} transition={{
              duration: 3,
              repeat: Infinity,
              ease: 'easeInOut',
              delay: 0.4
            }} data-id="element-116">
                🔥
              </motion.span>
              <motion.span className="absolute bottom-1/3 -left-4 md:-left-6 text-3xl md:text-4xl z-10 select-none" animate={{
              y: [0, -6, 0]
            }} transition={{
              duration: 2.8,
              repeat: Infinity,
              ease: 'easeInOut',
              delay: 0.8
            }} data-id="element-117">
                ⚡
              </motion.span>
              <motion.span className="absolute bottom-12 -right-2 md:-right-4 text-3xl md:text-4xl z-10 select-none" animate={{
              y: [0, -8, 0]
            }} transition={{
              duration: 2.6,
              repeat: Infinity,
              ease: 'easeInOut',
              delay: 1.2
            }} data-id="element-118">
                🏋️
              </motion.span>
              <motion.span className="absolute -bottom-2 left-1/4 text-2xl md:text-3xl z-10 select-none" animate={{
              y: [0, -6, 0]
            }} transition={{
              duration: 3.2,
              repeat: Infinity,
              ease: 'easeInOut',
              delay: 0.6
            }} data-id="element-119">
                ✨
              </motion.span>

              <div className="rounded-3xl overflow-hidden" data-id="element-120">
                <img src="https://cdn.magicpatterns.com/uploads/ag23ct9kWouFiK263FE4QW/Beige_Khaki_Journal_Page_and_Download_Mockup_Facebook_Post.jpg" alt="Ebook Ejercitá en Casa - Fundamentos, Nutrición y Mentalidad" className="w-full h-auto object-contain drop-shadow-2xl" data-id="element-121" />
              </div>
            </div>

            {/* Decorative dots */}
            <div className="absolute -top-6 -right-6 w-24 h-24 bg-[radial-gradient(#f43f5e_2px,transparent_2px)] [background-size:12px_12px] opacity-20 -z-10" data-id="element-122" />
            <div className="absolute -bottom-6 -left-6 w-32 h-32 bg-[radial-gradient(#f43f5e_2px,transparent_2px)] [background-size:12px_12px] opacity-20 -z-10" data-id="element-123" />
          </motion.div>
        </div>
      </div>
    </section>;
}